import React, { useState, useEffect } from 'react';
import { Box, Typography, Container, Chip, Input, Grid, CircularProgress, Button } from '@mui/joy';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SearchIcon from '@mui/icons-material/Search';

import { useBlogList, usePublishedBlogTags } from './useBlogs';

const formatDate = (dateStr) => {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const BlogAllPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTag, setActiveTag] = useState(null);

  // Accumulated items across cursor pages for current filter
  const [accumulatedItems, setAccumulatedItems] = useState([]);
  const [currentAfter, setCurrentAfter] = useState(null);

  // Always load the complete set of tags (lightweight)
  const allTags = usePublishedBlogTags();

  // Server-driven page for current filters
  const list = useBlogList({
    limit: 20,
    after: currentAfter,
    tag: activeTag,
    q: searchQuery || null,
  });

  // Reset accumulation when search or tag filter changes
  useEffect(() => {
    setAccumulatedItems([]);
    setCurrentAfter(null);
  }, [searchQuery, activeTag]);

  // Accumulate newly loaded page
  useEffect(() => {
    if (!list.loading) {
      if (currentAfter === null) {
        setAccumulatedItems(list.items || []);
      } else {
        setAccumulatedItems((prev) => [...prev, ...(list.items || [])]);
      }
    }
  }, [list.items, list.loading, currentAfter]);

  const handleLoadMore = () => {
    if (list.has_more && list.next_cursor) {
      setCurrentAfter(list.next_cursor);
    }
  };

  if (list.loading && accumulatedItems.length === 0) {
    return (
      <Box sx={{ py: 12, minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ py: 12, minHeight: '100vh' }}>
      <Helmet>
        <title>Blog | Calvet Technology Solutions</title>
        <meta
          name="description"
          content="Insights on AI, software development, cloud infrastructure, and digital transformation from Calvet Technology Solutions."
        />
        <meta property="og:title" content="Blog | Calvet Technology Solutions" />
        <meta
          property="og:description"
          content="Insights on AI, software development, cloud infrastructure, and digital transformation from Calvet Technology Solutions."
        />
        <meta property="og:type" content="website" />
      </Helmet>

      <Container maxWidth="lg">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              level="h1"
              sx={{
                mb: 3,
                fontWeight: 700,
                background: 'linear-gradient(135deg, #1B365D 0%, #2E5BBA 50%, #00A86B 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Insights & Articles
            </Typography>
            <Typography
              level="h4"
              sx={{
                color: 'text.secondary',
                maxWidth: 700,
                mx: 'auto',
                lineHeight: 1.6,
                fontWeight: 400,
              }}
            >
              Practical perspectives on AI, software engineering, cloud infrastructure,
              and building technology that drives real business results.
            </Typography>
          </Box>
        </motion.div>

        {/* Search */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Box sx={{ maxWidth: 500, mx: 'auto', mb: 4 }}>
            <Input
              placeholder="Search articles..."
              startDecorator={<SearchIcon />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                '&:hover': {
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                },
                '&:focus-within': {
                  border: '1px solid #2E5BBA',
                },
              }}
            />
          </Box>
        </motion.div>

        {/* Tag Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              justifyContent: 'center',
              mb: 6,
            }}
          >
            <Chip
              variant={activeTag === null ? 'solid' : 'outlined'}
              onClick={() => setActiveTag(null)}
              sx={{
                cursor: 'pointer',
                ...(activeTag === null
                  ? {
                    background: '#2E5BBA',
                    color: 'white',
                  }
                  : {
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                    color: 'text.secondary',
                    '&:hover': {
                      borderColor: '#2E5BBA',
                      color: '#2E5BBA',
                    },
                  }),
              }}
            >
              All
            </Chip>
            {allTags.map((tag) => (
              <Chip
                key={tag}
                variant={activeTag === tag ? 'solid' : 'outlined'}
                onClick={() => setActiveTag(activeTag === tag ? null : tag)}
                sx={{
                  cursor: 'pointer',
                  ...(activeTag === tag
                    ? {
                      background: '#2E5BBA',
                      color: 'white',
                    }
                    : {
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                      color: 'text.secondary',
                      '&:hover': {
                        borderColor: '#2E5BBA',
                        color: '#2E5BBA',
                      },
                    }),
                }}
              >
                {tag}
              </Chip>
            ))}
          </Box>
        </motion.div>

        {/* Post Grid */}
        <Grid container spacing={4}>
          {accumulatedItems.map((post, index) => (
            <Grid key={post.slug} xs={12} md={6} lg={4}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index % 3 * 0.1 }}
                whileHover={{ y: -5 }}
                style={{ height: '100%' }}
              >
                <Box
                  sx={{
                    p: 4,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    background: 'rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                    },
                  }}
                >
                  <Typography
                    level="body2"
                    sx={{
                      color: '#2E5BBA',
                      fontWeight: 600,
                      mb: 1.5,
                      fontSize: '0.85rem',
                    }}
                  >
                    {formatDate(post.date)}
                  </Typography>

                  <Typography
                    level="h4"
                    sx={{ mb: 2, fontWeight: 600 }}
                  >
                    <Link
                      to={`/blog/${post.slug}`}
                      style={{
                        textDecoration: 'none',
                        color: '#ffffff',
                      }}
                      onMouseEnter={(e) => { e.target.style.color = '#2E5BBA'; }}
                      onMouseLeave={(e) => { e.target.style.color = '#ffffff'; }}
                    >
                      {post.title}
                    </Link>
                  </Typography>

                  <Typography
                    level="body2"
                    sx={{
                      color: 'text.secondary',
                      lineHeight: 1.6,
                      mb: 3,
                      flex: 1,
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {post.excerpt}
                  </Typography>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {post.tags && post.tags.map((tag) => (
                      <Chip
                        key={tag}
                        size="sm"
                        variant="soft"
                        sx={{
                          background: 'rgba(46, 91, 186, 0.1)',
                          color: '#2E5BBA',
                          border: '1px solid rgba(46, 91, 186, 0.2)',
                          fontSize: '0.75rem',
                        }}
                      >
                        {tag}
                      </Chip>
                    ))}
                  </Box>
                </Box>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Load more */}
        {list.has_more && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
            <Button
              variant="outlined"
              onClick={handleLoadMore}
              disabled={list.loading}
              sx={{
                borderColor: 'rgba(255,255,255,0.2)',
                color: 'text.primary',
                '&:hover': { borderColor: '#2E5BBA', color: '#2E5BBA' },
              }}
            >
              {list.loading ? 'Loading…' : 'Load more articles'}
            </Button>
          </Box>
        )}

        {/* Empty State */}
        {accumulatedItems.length === 0 && !list.loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
          >
            <Box sx={{ textAlign: 'center', py: 10 }}>
              <Typography level="h4" sx={{ color: 'text.secondary', mb: 2 }}>
                No articles found
              </Typography>
              <Typography level="body1" sx={{ color: 'text.secondary' }}>
                Try adjusting your search or clearing the tag filter.
              </Typography>
            </Box>
          </motion.div>
        )}
      </Container>
    </Box>
  );
};

export default BlogAllPage;
